import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, MapPin, CalendarDays, CalendarCheck,
  Tag, CreditCard, Bell, LogOut, Plus, School,
  ShieldCheck, BookOpen, Dumbbell, Calendar, GraduationCap,
  BarChart3, Ticket, Mail, History, Sparkles, TrendingUp,
  FileText, LifeBuoy, ExternalLink, Image as ImageIcon,
  ChevronDown, Sliders, UserRound, CalendarOff,
  Trophy,
  Tent, ChevronLeft, ChevronRight, Wrench,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Separator } from '@/components/ui/separator';
import { SupportContactDialog } from '@/components/support/SupportContactDialog';
import { SideNavTooltip } from '@/components/SideNavTooltip';
import { SideNavGroupDropdown } from '@/components/SideNavGroupDropdown';
import { SideNavMoreMenu } from '@/components/SideNavMoreMenu';
import { useSideNavCollapsed } from '@/hooks/useSideNavCollapsed';
import { useSchool, useMySchools, getAccessToken, isMasterAdmin, useAuthStore, useGetSchoolCreationAllowed, useIndividualClassSettings } from '@sportschool/shared-logic';
import { confirmAndLogout } from '@/lib/logout';
import { useState, useEffect, useMemo } from 'react';

interface NavItem {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  module: string;
}

interface NavGroup {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  children: NavItem[];
}

type NavEntry =
  | { type: 'item'; item: NavItem }
  | { type: 'group'; group: NavGroup };

interface Props {
  schoolId?: string;
  isMobile?: boolean;
  onClose?: () => void;
}

export function SideNavBar({ schoolId, isMobile, onClose }: Props) {
  const { t } = useTranslation();
  const location = useLocation();
  const { data: school } = useSchool(schoolId ?? '');
  const rawSlogan = school?.slogan ?? '';
  const hideSlogan = rawSlogan.startsWith('[hide-slogan]');
  const [isMaster, setIsMaster] = useState(false);
  const { data: mySchools } = useMySchools();
  const hasMultipleSchools = (mySchools?.length ?? 0) > 1;
  const { data: creationAllowedData } = useGetSchoolCreationAllowed();
  const isCreationAllowed = creationAllowedData?.isAllowed ?? false;

  useEffect(() => {
    getAccessToken().then((token) => {
      setIsMaster(isMasterAdmin(token));
    });
  }, []);

  const [logoLoaded, setLogoLoaded] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [storedCollapsed, setCollapsed] = useSideNavCollapsed();
  // The mobile drawer always shows full labels; collapsing applies to the desktop sidebar only.
  const collapsed = !isMobile && storedCollapsed;

  useEffect(() => {
    setLogoLoaded(false);
    setLogoError(false);
  }, [school?.logoUrl]);

  const userContext = useAuthStore((s) => s.userContext);
  const activeRole = useAuthStore((s) => s.activeRole);
  const hasIndividualClassesFeature = !!userContext?.activeSchool?.enabledFeatures?.includes('IndividualClasses');
  const { data: individualSettings } = useIndividualClassSettings(hasIndividualClassesFeature && schoolId ? schoolId : '');
  const canManageIndividualSlots = !!individualSettings?.canManageSlots;
  const canManageRacketStringing = !!userContext?.activeSchool?.canManageRacketStringing;

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    const initial: Record<string, boolean> = {};
    if (path.includes('/payments') || path.includes('/coach-reports')) initial.billing = true;
    if (path.includes('/makeup-credits') || path.includes('/coupons')) initial.vouchers = true;
    if (
      path.includes('/statistics') ||
      path.includes('/locations') ||
      path.includes('/calendar') ||
      path.includes('/promos') ||
      path.includes('/media') ||
      path.includes('/pricelists') ||
      path.includes('/mailing') ||
      path.includes('/notifications')
    ) {
      initial.system = true;
    }
    return initial;
  });

  const toggleGroup = (groupId: string) => {
    setOpenGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  const filteredNavEntries = useMemo((): NavEntry[] => {
    if (!schoolId) return [];

    const isLoaded = !schoolId || (!!userContext && userContext.activeSchool?.id === schoolId);
    const enabledModules = isLoaded && userContext?.activeSchool?.enabledModules
      ? userContext.activeSchool.enabledModules
      : [];
    const enabledFeatures = isLoaded && userContext?.activeSchool?.enabledFeatures
      ? userContext.activeSchool.enabledFeatures
      : [];

    const isItemAllowed = (item: NavItem) => {
      const isCoachModule = ['coachReports', 'coachProfile', 'coachPastSessions'].includes(item.module);
      const isStudentModule = ['studentEnrollments', 'studentCamps'].includes(item.module);
      const isIndividualModule = ['individualClasses', 'coachIndividualClasses'].includes(item.module);
      const isRacketStringingModule = item.module === 'racketStringing';
      if (item.module && !isCoachModule && !isStudentModule && !isIndividualModule && !isRacketStringingModule && !enabledModules.includes(item.module)) return false;

      // Individual classes: feature flag, and the coach panel only for admins / enabled coaches
      if (isIndividualModule && !enabledFeatures.includes('IndividualClasses')) return false;
      if (item.module === 'coachIndividualClasses' && !canManageIndividualSlots) return false;

      // Filter by features
      if (item.to.includes('/mailing') && !enabledFeatures.includes('Mailing')) return false;
      if (item.to.includes('/coupons') && !enabledFeatures.includes('Coupons')) return false;
      if (item.module === 'tournaments' && !enabledFeatures.includes('Tournaments')) return false;
      if ((item.module === 'camps' || item.module === 'studentCamps') && !enabledFeatures.includes('Camps')) return false;
      // Racket stringing: feature flag; coaches only with the permission granted by the admin
      if (isRacketStringingModule && !enabledFeatures.includes('RacketStringing')) return false;
      if (isRacketStringingModule && activeRole === 'Coach' && !canManageRacketStringing) return false;

      if (activeRole === 'Student') {
        if (item.to.includes('/coupons') || item.to.includes('/makeup-credits')) return false;
        return ['classes', 'studentPortal', 'studentEnrollments', 'studentCamps', 'payments', 'locations', 'individualClasses', 'racketStringing'].includes(item.module);
      } else if (activeRole === 'Coach') {
        if (item.to.includes('/media')) return false;
        return ['dashboard', 'notifications', 'coachReports', 'coachProfile', 'coachPastSessions', 'coachIndividualClasses', 'tournaments', 'racketStringing'].includes(item.module);
      } else if (activeRole === 'Admin') {
        return !['studentPortal', 'studentEnrollments', 'studentCamps', 'classes', 'coachPortal', 'coachProfile', 'coachPastSessions', 'individualClasses'].includes(item.module);
      }

      return true;
    };

    if (activeRole === 'Admin') {
      const adminStructure: Array<
        | { type: 'item'; item: NavItem }
        | { type: 'group'; group: NavGroup }
      > = [
        { type: 'item', item: { to: `/schools/${schoolId}/dashboard`, icon: LayoutDashboard, label: t('nav.dashboard'), module: 'dashboard' } },
        { type: 'item', item: { to: `/schools/${schoolId}/schedules`, icon: CalendarDays, label: t('nav.schedules'), module: 'schedules' } },
        { type: 'item', item: { to: `/schools/${schoolId}/sessions`, icon: CalendarCheck, label: t('nav.sessions'), module: 'sessions' } },
        { type: 'item', item: { to: `/schools/${schoolId}/coach/individual-classes`, icon: UserRound, label: t('nav.coachIndividualClasses'), module: 'coachIndividualClasses' } },
        { type: 'item', item: { to: `/schools/${schoolId}/students`, icon: GraduationCap, label: t('nav.students', 'Zawodnicy'), module: 'users' } },
        { type: 'item', item: { to: `/schools/${schoolId}/coaches`, icon: Dumbbell, label: t('nav.coaches'), module: 'users' } },
        { type: 'item', item: { to: `/schools/${schoolId}/tournaments`, icon: Trophy, label: t('nav.tournaments'), module: 'tournaments' } },
        { type: 'item', item: { to: `/schools/${schoolId}/camps`, icon: Tent, label: t('nav.camps'), module: 'camps' } },
        { type: 'item', item: { to: `/schools/${schoolId}/racket-stringing`, icon: Wrench, label: t('nav.racketStringing'), module: 'racketStringing' } },
        {
          type: 'group',
          group: {
            id: 'billing',
            label: t('nav.groupBilling', 'Rozliczenia'),
            icon: CreditCard,
            children: [
              { to: `/schools/${schoolId}/payments`, icon: CreditCard, label: t('nav.payments'), module: 'payments' },
              { to: `/schools/${schoolId}/coach-reports`, icon: BarChart3, label: t('nav.coachReports', 'Podsumowanie godzin'), module: 'coachReports' },
            ],
          },
        },
        {
          type: 'group',
          group: {
            id: 'vouchers',
            label: t('nav.groupVouchers', 'Vouchery'),
            icon: Ticket,
            children: [
              { to: `/schools/${schoolId}/makeup-credits`, icon: CalendarDays, label: t('nav.makeupCredits', 'Vouchery na odrabianie'), module: 'payments' },
              { to: `/schools/${schoolId}/coupons`, icon: Ticket, label: t('nav.coupons', 'Vouchery rabatowe'), module: 'payments' },
            ],
          },
        },
        {
          type: 'group',
          group: {
            id: 'system',
            label: t('nav.groupSystem', 'System'),
            icon: Sliders,
            children: [
              { to: `/schools/${schoolId}/statistics`, icon: TrendingUp, label: t('nav.statistics'), module: 'dashboard' },
              { to: `/schools/${schoolId}/locations`, icon: MapPin, label: t('nav.locations'), module: 'locations' },
              { to: `/schools/${schoolId}/calendar`, icon: CalendarOff, label: t('nav.calendar'), module: 'locations' },
              { to: `/schools/${schoolId}/promos`, icon: Sparkles, label: t('nav.promos', 'Promo'), module: 'promos' },
              { to: `/schools/${schoolId}/media`, icon: ImageIcon, label: t('nav.media', 'Media'), module: 'dashboard' },
              { to: `/schools/${schoolId}/pricelists`, icon: Tag, label: t('nav.priceLists'), module: 'pricelists' },
              { to: `/schools/${schoolId}/mailing`, icon: Mail, label: t('nav.mailing', 'Mailing i Automatyzacja'), module: 'mailing' },
              { to: `/schools/${schoolId}/notifications`, icon: Bell, label: t('nav.notifications'), module: 'notifications' },
            ],
          },
        },
      ];

      const filtered: NavEntry[] = [];
      for (const entry of adminStructure) {
        if (entry.type === 'item') {
          if (isItemAllowed(entry.item)) {
            filtered.push(entry);
          }
        } else {
          const allowedChildren = entry.group.children.filter(isItemAllowed);
          if (allowedChildren.length > 0) {
            filtered.push({
              type: 'group',
              group: {
                ...entry.group,
                children: allowedChildren,
              },
            });
          }
        }
      }
      return filtered;
    }

    // Default / Student / Coach flat items
    const navItems: NavItem[] = [
      { to: `/schools/${schoolId}/dashboard`, icon: LayoutDashboard, label: t('nav.dashboard'), module: 'dashboard' },
      { to: `/schools/${schoolId}/locations`, icon: MapPin, label: t('nav.locations'), module: 'locations' },
      { to: `/schools/${schoolId}/schedules`, icon: CalendarDays, label: t('nav.schedules'), module: 'schedules' },
      { to: `/schools/${schoolId}/sessions`, icon: CalendarCheck, label: t('nav.sessions'), module: 'sessions' },
      { to: `/schools/${schoolId}/students`, icon: GraduationCap, label: t('nav.students', 'Zawodnicy'), module: 'users' },
      { to: `/schools/${schoolId}/coaches`, icon: Dumbbell, label: t('nav.coaches'), module: 'users' },
      { to: `/schools/${schoolId}/pricelists`, icon: Tag, label: t('nav.priceLists'), module: 'pricelists' },
      { to: `/schools/${schoolId}/coupons`, icon: Ticket, label: t('nav.coupons', 'Vouchery rabatowe'), module: 'payments' },
      { to: `/schools/${schoolId}/media`, icon: ImageIcon, label: t('nav.media', 'Media'), module: 'dashboard' },
      { to: `/schools/${schoolId}/makeup-credits`, icon: CalendarDays, label: t('nav.makeupCredits', 'Vouchery na odrabianie'), module: 'payments' },
      { to: `/schools/${schoolId}/promos`, icon: Sparkles, label: t('nav.promos', 'Promo'), module: 'promos' },
      { to: `/schools/${schoolId}/student`, icon: BookOpen, label: t('nav.studentPortal'), module: 'studentPortal' },
      { to: `/schools/${schoolId}/student/enrollments`, icon: History, label: t('nav.studentEnrollments', 'Historia zapisów'), module: 'studentEnrollments' },
      { to: `/schools/${schoolId}/student/camps`, icon: Tent, label: t('nav.camps'), module: 'studentCamps' },
      { to: `/schools/${schoolId}/notifications`, icon: Bell, label: t('nav.notifications'), module: 'notifications' },
      { to: `/schools/${schoolId}/mailing`, icon: Mail, label: t('nav.mailing', 'Mailing i Automatyzacja'), module: 'mailing' },
      { to: `/schools/${schoolId}/classes`, icon: Calendar, label: t('nav.classes'), module: 'classes' },
      { to: `/schools/${schoolId}/individual-classes`, icon: UserRound, label: t('nav.individualClasses'), module: 'individualClasses' },
      { to: `/schools/${schoolId}/coach/individual-classes`, icon: UserRound, label: t('nav.coachIndividualClasses'), module: 'coachIndividualClasses' },
      { to: `/schools/${schoolId}/payments`, icon: CreditCard, label: t('nav.payments'), module: 'payments' },
      { to: `/schools/${schoolId}/coach-reports`, icon: BarChart3, label: t('nav.coachReports', 'Rozliczenia trenerów'), module: 'coachReports' },
      { to: `/schools/${schoolId}/tournaments`, icon: Trophy, label: t('nav.tournaments'), module: 'tournaments' },
      { to: `/schools/${schoolId}/camps`, icon: Tent, label: t('nav.camps'), module: 'camps' },
      { to: `/schools/${schoolId}/racket-stringing`, icon: Wrench, label: t('nav.racketStringing'), module: 'racketStringing' },
      { to: `/schools/${schoolId}/coach`, icon: Dumbbell, label: t('nav.coachPortal'), module: 'coachPortal' },
      { to: `/schools/${schoolId}/coach/past-sessions`, icon: History, label: t('nav.coachPastSessions', 'Poprzednie treningi'), module: 'coachPastSessions' },
      { to: `/schools/${schoolId}/coaches/${userContext?.activeSchool?.memberId}`, icon: Dumbbell, label: t('nav.myProfile', 'Moja dostępność'), module: 'coachProfile' },
    ];

    const items = navItems.filter(isItemAllowed);

    if (activeRole === 'Student') {
      const order = ['student', 'classes', 'individual-classes', 'student/camps', 'racket-stringing', 'locations', 'student/enrollments', 'payments'];
      items.sort((a, b) => {
        const aKey = a.to.split('/').slice(3).join('/');
        const bKey = b.to.split('/').slice(3).join('/');
        const aIdx = order.indexOf(aKey);
        const bIdx = order.indexOf(bKey);
        return (aIdx === -1 ? 99 : aIdx) - (bIdx === -1 ? 99 : bIdx);
      });
    }

    return items.map((item) => ({ type: 'item' as const, item }));
  }, [schoolId, userContext, activeRole, t, canManageIndividualSlots, canManageRacketStringing]);

  useEffect(() => {
    filteredNavEntries.forEach((entry) => {
      if (entry.type === 'group') {
        const isChildActive = entry.group.children.some((child) => {
          return child.to.includes('?')
            ? location.pathname + location.search === child.to
            : location.pathname === child.to || location.pathname.startsWith(child.to + '/');
        });
        if (isChildActive) {
          setOpenGroups((prev) => (prev[entry.group.id] ? prev : { ...prev, [entry.group.id]: true }));
        }
      }
    });
  }, [location.pathname, location.search, filteredNavEntries]);

  const isPathActive = (to: string) =>
    to.includes('?')
      ? location.pathname + location.search === to
      : location.pathname === to || location.pathname.startsWith(to + '/');

  const itemClass = (isActive: boolean) =>
    cn(
      'flex items-center gap-3 rounded-lg text-sm font-medium transition-colors',
      collapsed ? 'justify-center h-11 px-0 shrink-0' : 'px-3 py-2',
      isActive
        ? 'bg-accent text-accent-foreground'
        : 'text-muted-foreground hover:text-foreground hover:bg-muted',
    );


  const footerButtonClass = cn(
    'flex items-center gap-3 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors w-full text-left cursor-pointer',
    collapsed ? 'justify-center h-11 px-0 shrink-0' : 'px-3 py-2',
  );

  const renderItemLink = (item: NavItem) => (
    <SideNavTooltip key={item.to} label={item.label} enabled={collapsed}>
      <NavLink
        to={item.to}
        onClick={onClose}
        aria-label={collapsed ? item.label : undefined}
        className={itemClass(isPathActive(item.to))}
      >
        <item.icon className="size-4 shrink-0" />
        {!collapsed && <span className="truncate">{item.label}</span>}
      </NavLink>
    </SideNavTooltip>
  );

  const regulationsLabel = activeRole === 'Admin' ? t('nav.regulationsAdmin', 'Regulaminy') : t('nav.regulations', 'Regulamin');
  const toggleLabel = collapsed ? t('nav.expandMenu') : t('nav.collapseMenu');
  // Small tab sticking out of the sidebar's right edge, vertically centred on the TopAppBar (~61px tall)
  const toggleTab = !isMobile && (
    <button
      type="button"
      onClick={() => setCollapsed(!collapsed)}
      aria-label={toggleLabel}
      title={toggleLabel}
      className="absolute top-[10px] left-full flex items-center justify-center w-5 h-10 -ml-px rounded-r-lg border border-l-0 bg-card text-muted-foreground shadow-[2px_1px_4px_rgba(0,0,0,0.08)] hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
    >
      {collapsed ? <ChevronRight className="size-3.5" /> : <ChevronLeft className="size-3.5" />}
    </button>
  );

  return (
    <nav className={cn(
      isMobile
        ? "flex flex-col h-full w-full bg-card py-5 overflow-y-auto no-scrollbar"
        : cn(
            // Above the sticky TopAppBar (z-40), so the collapse tab sticking out over it stays visible
            "hidden md:flex flex-col sticky top-0 left-0 h-screen border-r bg-card shrink-0 py-5 z-[45] transition-[width] duration-200",
            collapsed ? "w-16" : "w-60",
          )
    )}>
      {toggleTab}

      {/* Logo (a small one when the sidebar is collapsed) */}
      {collapsed ? (
        <div className="flex items-center justify-center w-full pb-3">
          {school?.logoUrl && !logoError ? (
            <img
              src={school.logoUrl}
              alt={school.name}
              onError={() => setLogoError(true)}
              className="size-11 object-contain"
            />
          ) : (
            <div className="flex items-center justify-center size-11 rounded-lg bg-primary text-primary-foreground text-sm font-bold">
              {school?.name?.substring(0, 2).toUpperCase() || 'CA'}
            </div>
          )}
        </div>
      ) : (
        <div className={cn("relative flex items-center w-full", hideSlogan ? "px-0 pb-3 justify-center" : "px-4 pb-5 gap-3")}>
          {school?.logoUrl && !logoError ? (
            <img
              src={school.logoUrl}
              alt={school.name}
              onLoad={() => setLogoLoaded(true)}
              onError={() => setLogoError(true)}
              className={cn(
                "object-contain transition-all duration-500 ease-out transform",
                hideSlogan ? "h-22 max-w-[208px] w-auto" : "h-9 w-auto shrink-0",
                logoLoaded ? "opacity-100 scale-100" : "opacity-0 scale-95"
              )}
            />
          ) : (
            <div
              className={cn(
                "flex items-center justify-center rounded-lg bg-primary text-primary-foreground shrink-0 font-bold transition-all duration-300",
                hideSlogan ? "size-22 text-2xl" : "size-9 text-sm"
              )}
            >
              {school?.name?.substring(0, 2).toUpperCase() || 'CA'}
            </div>
          )}
          {!hideSlogan && (
            <div className="min-w-0">
              <p className="text-sm font-bold text-foreground leading-tight">{school?.name ?? 'SportSchool'}</p>
              <p className="text-xs text-muted-foreground">{t('nav.adminPortal')}</p>
            </div>
          )}
        </div>
      )}

      <Separator />

      {/* Main nav */}
      <div className={cn("flex-1 overflow-y-auto no-scrollbar flex flex-col gap-0.5 pt-2", collapsed ? "px-2.5" : "px-3")}>
        {schoolId ? (
          filteredNavEntries.map((entry) => {
            if (entry.type === 'item') {
              return renderItemLink(entry.item);
            }

            const group = entry.group;

            const isGroupActive = group.children.some((child) => isPathActive(child.to));

            // Collapsed: no room for an expandable header, so the group's items open in a dropdown
            if (collapsed) {
              return (
                <SideNavGroupDropdown
                  key={group.id}
                  label={group.label}
                  icon={group.icon}
                  items={group.children}
                  isPathActive={isPathActive}
                  triggerClassName={itemClass(isGroupActive)}
                />
              );
            }

            const isOpen = !!openGroups[group.id];

            return (
              <div key={group.id} className="flex flex-col">
                <button
                  type="button"
                  onClick={() => toggleGroup(group.id)}
                  className={cn(
                    'flex items-center justify-between w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer text-left',
                    isGroupActive
                      ? 'text-foreground font-semibold bg-muted/40'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted',
                  )}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <group.icon className="size-4 shrink-0" />
                    <span className="truncate">{group.label}</span>
                  </div>
                  <ChevronDown
                    className={cn(
                      'size-4 shrink-0 text-muted-foreground transition-transform duration-200',
                      isOpen && 'rotate-180',
                    )}
                  />
                </button>
                {isOpen && (
                  <div className="flex flex-col gap-0.5 ml-4 pl-3 border-l border-border/60 my-0.5">
                    {group.children.map((child) => {
                      const isChildActive = isPathActive(child.to);

                      return (
                        <NavLink
                          key={child.to}
                          to={child.to}
                          onClick={onClose}
                          className={cn(
                            'flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-sm transition-colors',
                            isChildActive
                              ? 'bg-accent text-accent-foreground font-medium'
                              : 'text-muted-foreground hover:text-foreground hover:bg-muted font-normal',
                          )}
                        >
                          <child.icon className="size-3.5 shrink-0 opacity-70" />
                          <span className="truncate">{child.label}</span>
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <SideNavTooltip label={t('nav.home')} enabled={collapsed}>
            <NavLink to="/" className={itemClass(isPathActive('/'))} onClick={onClose} aria-label={collapsed ? t('nav.home') : undefined}>
              <LayoutDashboard className="size-4 shrink-0" />
              {!collapsed && t('nav.home')}
            </NavLink>
          </SideNavTooltip>
        )}
      </div>

      <Separator />

      {/* Footer nav */}
      <div className={cn("pt-3 flex flex-col gap-0.5", collapsed ? "px-2.5" : "px-3")}>
        {isMaster && (
          <SideNavTooltip label={t('nav.masterAdmin')} enabled={collapsed}>
            <NavLink to="/admin" className={itemClass(isPathActive('/admin'))} onClick={onClose} aria-label={collapsed ? t('nav.masterAdmin') : undefined}>
              <ShieldCheck className="size-4 shrink-0" />
              {!collapsed && t('nav.masterAdmin')}
            </NavLink>
          </SideNavTooltip>
        )}
        {hasMultipleSchools && (
          <SideNavTooltip label={t('nav.switchSchool')} enabled={collapsed}>
            <NavLink to="/select-school" className={itemClass(isPathActive('/select-school'))} onClick={onClose} aria-label={collapsed ? t('nav.switchSchool') : undefined}>
              <School className="size-4 shrink-0" />
              {!collapsed && t('nav.switchSchool')}
            </NavLink>
          </SideNavTooltip>
        )}
        {isCreationAllowed && (
          <SideNavTooltip label={t('nav.createSchool')} enabled={collapsed}>
            <NavLink to="/schools/new" className={itemClass(isPathActive('/schools/new'))} onClick={onClose} aria-label={collapsed ? t('nav.createSchool') : undefined}>
              <Plus className="size-4 shrink-0" />
              {!collapsed && t('nav.createSchool')}
            </NavLink>
          </SideNavTooltip>
        )}
        {collapsed ? (
          <SideNavMoreMenu
            instructionUrl={schoolId ? school?.instructionUrl : undefined}
            regulationsTo={schoolId ? `/schools/${schoolId}/support/regulations` : undefined}
            regulationsLabel={regulationsLabel}
            onHelp={schoolId ? () => setHelpDialogOpen(true) : undefined}
            onLogout={() => void confirmAndLogout()}
            triggerClassName={footerButtonClass}
          />
        ) : (
          <>
            {schoolId && school?.instructionUrl && (
              <a
                href={school.instructionUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onClose}
                className={footerButtonClass}
              >
                <FileText className="size-4 shrink-0" />
                <span>{t('nav.instruction', 'Instrukcja')}</span>
                <ExternalLink className="size-3.5 ml-auto opacity-70" />
              </a>
            )}
            {schoolId && (
              <button
                type="button"
                onClick={() => {
                  if (onClose) onClose();
                  setHelpDialogOpen(true);
                }}
                className={footerButtonClass}
              >
                <LifeBuoy className="size-4 shrink-0" />
                <span>{t('nav.help', 'Pomoc')}</span>
              </button>
            )}
            {schoolId && (
              <NavLink to={`/schools/${schoolId}/support/regulations`} className={itemClass(isPathActive(`/schools/${schoolId}/support/regulations`))} onClick={onClose}>
                <BookOpen className="size-4 shrink-0" />
                {regulationsLabel}
              </NavLink>
            )}
            <button
              onClick={() => {
                if (onClose) onClose();
                void confirmAndLogout();
              }}
              className={cn(footerButtonClass, 'hover:text-destructive hover:bg-destructive/10')}
            >
              <LogOut className="size-4 shrink-0" />
              {t('nav.logout')}
            </button>
          </>
        )}
      </div>

      {schoolId && (
        <SupportContactDialog
          open={helpDialogOpen}
          onOpenChange={setHelpDialogOpen}
          schoolId={schoolId}
        />
      )}
    </nav>
  );
}
